import React, { useEffect, useState } from "react";
import axios from "axios";
import { useRoutes, useNavigate, useLocation } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { create } from "jss";

import jssPreset from "@mui/styles/jssPreset";

import "./i18n";

import routes from "./routes";

import { AuthProvider } from "./contexts/JWTContext";

import useUserStore from "./services/userStore";

import { myLocalStorage } from "./components/StorageHelper";
import { QueryClient, QueryClientProvider } from "react-query";
import UserService, { useKeycloakStore } from "./services/UserService";
import {
  decodeKeycloakToken,
  storeLatestTenant,
} from "./utils/tokenHelpers";
import {
  registerSessionTokens,
  startSessionTokenWatcher,
  stopSessionTokenWatcher,
} from "./utils/sessionTokenManager";
import { clearLocalStorageOnLogout, usersRoutes } from "./services/Helpers";
import { channel, logOut } from "./services/Auth";
import { Alert, Snackbar } from "@mui/material";
import { CacheProvider } from "./utils/CacheContext";
import { ThemeProvider } from "./components/lib/theme-provider";

const jss = create({
  ...jssPreset(),
  insertionPoint: document.getElementById("jss-insertion-point"),
});

const App = () => {
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code") || "";
  const rawFromAzureSSO =
    params.get("fromAzureSSO") ??
    params.get("fromAzureSSo") ??
    params.get("fromazuresso");
  const normalizedAzureParam = `${rawFromAzureSSO || ""}`.toLowerCase();
  const isFromAzureSSO =
    rawFromAzureSSO !== null &&
    normalizedAzureParam !== "false" &&
    normalizedAzureParam !== "0";

  const navigate = useNavigate();

  const [servererror, setServerError] = useState(false);

  const location = useLocation();

  const content = useRoutes(routes);
  const userData = useUserStore((state) => state.user);
  const fetchUserData = useUserStore((state) => state.getUserData);

  const lastVisitedPage = myLocalStorage.getItem("lastVisitedPage");
  const access_token =
    UserService?.getToken() || myLocalStorage.getItem("access_token");

  const realm = myLocalStorage.getItem("realm");

  const logOutDueToTokenExpire = () => {
    const email = myLocalStorage.getItem("email");
    return logOut(email)
      .then(() => {
        clearLocalStorageOnLogout();
        navigate("/signIn");
      })
      .catch((error) => {
        console.error("An error occurred during logout:", error);
      });
  };

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
      },
    },
  });

  useEffect(() => {
    const allowedPaths = [
      "/",
      "/signUp",
      "/signIn",
      "/forgot-password",
      "/reset-password",
      "null",
      "/terms_of_service",
      "/loading",
      "/404",
    ];
    const currentPath = location.pathname;

    if (!allowedPaths.includes(currentPath)) {
      myLocalStorage.setItem("lastVisitedPage", currentPath);
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    try {
      if (!access_token) return;

      const currentUser = useUserStore.getState().user || {};
      const fetchedRecently =
        currentUser._fetchedAt &&
        Date.now() - currentUser._fetchedAt < 60_000;
      if (currentUser.userId || fetchedRecently) return;

      let userEmail = "";
      if (myLocalStorage.getItem("azuresso")) {
        userEmail = myLocalStorage.getItem("email");
      } else {
        const decodedToken = decodeKeycloakToken(access_token);
        userEmail = decodedToken.email;
        if (decodedToken?.iss.includes("-sso.local")) {
          myLocalStorage.setItem("role", "TENANT_USER");
        }
      }
      fetchUserData(userEmail);
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  }, [access_token, fetchUserData]);

  useEffect(() => {
    if (access_token) {
      registerSessionTokens(
        access_token,
        myLocalStorage.getItem("refresh_token"),
      );
    } else {
      stopSessionTokenWatcher();
    }
  }, [access_token]);

  useEffect(() => {
    const navigateOnRefresh = (user) => {
      const currentPath = location.pathname;
      const postAuthPaths = ["/loading", "/signIn", "/", "/signUp"];
      if (!postAuthPaths.includes(currentPath)) {
        return;
      }

      const role = user?.role || myLocalStorage.getItem("role");
      if (!role) return;

      if (role !== "TENANT_USER") {
        navigate(lastVisitedPage ? lastVisitedPage : "/endpoints");
      } else {
        navigate(
          lastVisitedPage && usersRoutes.includes(lastVisitedPage)
            ? lastVisitedPage
            : "/endpoints",
        );
      }
    };

    const role = userData?.role || myLocalStorage.getItem("role");
    if (role && access_token) {
      storeLatestTenant().finally(() => {
        navigateOnRefresh({ ...userData, role });
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);


  useEffect(() => {
    const processAuthCallback = async () => {
      if (isFromAzureSSO) {
        const email = myLocalStorage.getItem("email");

        if (!access_token && code) {
          if (!email) {
            setServerError(true);
            return;
          }

          myLocalStorage.setItem("azuresso", true);
          const isExchangeSuccess = await UserService.exchangeCodeForToken(
            code,
            email,
          );

          if (!isExchangeSuccess) {
            setServerError(true);
            return;
          }
        }

        if (code) {
          // Drop OAuth callback params so later tab switches never reuse stale code.
          navigate(location.pathname, { replace: true });
        }

        return;
      }

      if (!access_token && realm) {
        myLocalStorage.removeItem("azuresso");
        UserService.initKeycloak(realm);
      }
    };

    processAuthCallback();
  }, [access_token, realm, code, isFromAzureSSO, navigate, location.pathname]);

  useEffect(() => {
    const logoutMultipleTabs = () => {
      channel.onmessage = () => {
        navigate("/signIn");
      };
    };
    logoutMultipleTabs();
  }, [navigate]);


  return (
    <>
      <QueryClientProvider client={queryClient}>
        <CacheProvider>
          <HelmetProvider>
            <Helmet
              titleTemplate="%s | White Swan Security"
              defaultTitle="White Swan Security"
            />

            <AuthProvider>{content}</AuthProvider>
          </HelmetProvider>
        </CacheProvider>
      </QueryClientProvider>
      <Snackbar
        open={servererror}
        autoHideDuration={6000}
        onClose={() => setServerError(false)}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setServerError(false)}
        >
          Azure sign-in callback could not be completed. Please try again.
        </Alert>
      </Snackbar>
    </>
  );
};

export default App;
